import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import MaintenancePage from '@/pages/maintenance/page';

interface MaintenanceConfig {
  enabled: boolean;
  start: string | null;
  end: string | null;
  title: string;
  message: string;
}

const ADMIN_PATHS = ['/dang-nhap'];

export default function MaintenanceGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [config, setConfig] = useState<MaintenanceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')
        .eq('group', 'maintenance');

      if (error) throw error;

      const map: Record<string, string> = {};
      (data || []).forEach((r) => {
        map[r.key] = r.value ?? '';
      });

      setConfig({
        enabled: map.maintenance_enabled === 'true',
        start: map.maintenance_start || null,
        end: map.maintenance_end || null,
        title: map.maintenance_title || 'Website đang bảo trì',
        message: map.maintenance_message || '',
      });

      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session) {
        const { data: adminData } = await supabase.rpc('is_admin_mgr');
        setIsAdmin(!!adminData);
      } else {
        setIsAdmin(false);
      }
    } catch {
      // Nếu không đọc được cấu hình, không chặn truy cập
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const configInterval = setInterval(load, 60000);
    const tickInterval = setInterval(() => setNow(Date.now()), 15000);
    return () => {
      clearInterval(configInterval);
      clearInterval(tickInterval);
    };
  }, [load]);

  if (loading) return null;

  const isAdminArea =
    location.pathname.startsWith('/quan-tri') || ADMIN_PATHS.includes(location.pathname);

  let active = false;
  if (config?.enabled) {
    const startTs = config.start ? new Date(config.start).getTime() : null;
    const endTs = config.end ? new Date(config.end).getTime() : null;
    const afterStart = startTs === null || Number.isNaN(startTs) || now >= startTs;
    const beforeEnd = endTs === null || Number.isNaN(endTs) || now <= endTs;
    active = afterStart && beforeEnd;
  }

  if (active && !isAdmin && !isAdminArea) {
    return (
      <MaintenancePage
        title={config?.title}
        message={config?.message}
        endAt={config?.end}
      />
    );
  }

  return <>{children}</>;
}